import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CurrencyInput } from "@/components/ui/CurrencyInput";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InfoTooltip } from "@/components/InfoTooltip";
import { RotateCcw, AlertTriangle } from "lucide-react";
import type { PaymentFrequency } from "@/lib/calculationEngine";
import { getPaymentsPerYear } from "@/lib/calculationEngine";
import { createFormatter, type Jurisdiction } from "@/lib/formatters";
import type { USACarrier } from "@/lib/usaCarrierData";
import { usaCarrierLabels } from "@/lib/usaCarrierData";
import type { SolveForPaymentResult } from "@/lib/paymentSolver";

export type RepaymentMode = 'rate' | 'payment' | 'interest_only' | null;

export interface MultiPolicyEntry {
  amount: number;
  startDate: string;
  policyNumber: string;
  useSameStartDate: boolean;
  useSameAnniversary: boolean;
  anniversaryMonth: number;
  anniversaryDay: number;
}

export interface LoanSetupData {
  loanAmount: number;
  loanTitle: string;
  loanDate: string;
  interestRate: number;
  hasExistingBalance: boolean;
  consolidateExisting: boolean;
  existingBalance: number;
  existingAccruedInterest: number;
  availableCashValue: number | null;
  policyAnniversaryMonth: number;
  policyAnniversaryDay: number;
  policyNumber: string;
  lifeInsured: string;
  disciplineRate: number;
  paymentFrequency: PaymentFrequency;
  numberOfPayments: number;
  termYears: number;
  termMonths: number;
  startDate: string;
  usaCarrier: USACarrier;
  // Dual mode fields
  repaymentMode: RepaymentMode;
  desiredPayment: number;
  precisionMode: boolean;
  // Investment purpose fields
  isInvestmentPurpose: boolean;
  interestTrackingBasis: 'policy' | 'intended';
  // Interest-only fields
  interestOnlyBasis: 'policy' | 'intended';
  // Multi-policy fields
  isMultiPolicy: boolean;
  multiPolicyCount: number;
  policyLoans: MultiPolicyEntry[];
}

function computePaymentCount(years: number, months: number, freq: PaymentFrequency): number {
  if (freq === 'endofterm') return 1;
  const totalMonths = years * 12 + months;
  const perYear = getPaymentsPerYear(freq);
  return Math.max(1, Math.round((totalMonths / 12) * perYear));
}

interface LoanSetupProps {
  data: LoanSetupData;
  onChange: (data: LoanSetupData) => void;
  targetPayment: number;
  jurisdiction: Jurisdiction;
  solvedPayment?: SolveForPaymentResult | null;
  loanId?: string;
}

export function LoanSetup({ data, onChange, targetPayment, jurisdiction, solvedPayment, loanId }: LoanSetupProps) {
  const { fmt, currencySymbol } = createFormatter(jurisdiction);
  const update = (partial: Partial<LoanSetupData>) => onChange({ ...data, ...partial });
  const isUSA = jurisdiction === 'usa';
  const isPaymentMode = data.repaymentMode === 'payment';
  const isInterestOnlyMode = data.repaymentMode === 'interest_only';
  const isModeSelected = data.repaymentMode !== null;

  const handleTermChange = (years: number, months: number, freq?: PaymentFrequency) => {
    const f = freq ?? data.paymentFrequency;
    const count = computePaymentCount(years, months, f);
    onChange({ ...data, termYears: years, termMonths: months, numberOfPayments: count, ...(freq ? { paymentFrequency: freq } : {}) });
  };

  const sliderStep = data.precisionMode ? 0.25 : 0.01;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Loan Details */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold flex items-center justify-between gap-2">
            <span>Loan Details</span>
            {loanId && (
              <span className="text-[10px] font-mono font-medium px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                {loanId}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isUSA && (
            <div>
              <Label>Carrier</Label>
              <Select value={data.usaCarrier} onValueChange={(v: USACarrier) => update({ usaCarrier: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(usaCarrierLabels) as USACarrier[]).map(key => (
                    <SelectItem key={key} value={key}>{usaCarrierLabels[key]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="loanTitle">Loan Name / Purpose<InfoTooltip content="A short name for this loan (e.g., '2026 Vehicle Purchase'). Shown alongside the auto-generated Loan ID on saved scenarios and reports." /></Label>
              <Input id="loanTitle" value={data.loanTitle} onChange={e => update({ loanTitle: e.target.value })} placeholder="e.g., 2026 Vehicle Purchase" />
            </div>
            <div>
              <Label htmlFor="loanAmount">Loan Amount ({currencySymbol})<InfoTooltip content="The total amount you are borrowing against your policy's cash value." /></Label>
              <CurrencyInput
                id="loanAmount"
                value={data.loanAmount === 0 && !data.hasExistingBalance ? null : data.loanAmount}
                onChange={(v) => update({ loanAmount: v ?? 0 })}
                min={0}
                currency={isUSA ? "USD" : "CAD"}
                locale={isUSA ? "en-US" : "en-CA"}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="loanDate">Loan Effective Date<InfoTooltip content="The date the insurance company processes the loan — interest starts accruing from this date." /></Label>
              <Input id="loanDate" type="date" value={data.loanDate} onChange={e => update({ loanDate: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="interestRate">Interest Rate (%)<InfoTooltip content="The annual interest rate charged by the insurance company on your policy loan." /></Label>
              <Input id="interestRate" type="number" step={0.01} min={0} max={25} value={data.interestRate || ''} onChange={e => update({ interestRate: Number(e.target.value) })} />
            </div>
          </div>

          {/* Existing Balance Toggle */}
          <div className="space-y-3 p-3 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Do you have an existing policy loan balance?</Label>
                <p className="text-[10px] text-muted-foreground">Toggle on if you already owe money against this policy.</p>
              </div>
              <Switch
                checked={data.hasExistingBalance}
                onCheckedChange={(checked) => update({ hasExistingBalance: checked, ...(!checked ? { existingBalance: 0, consolidateExisting: false } : {}) })}
              />
            </div>

            {data.hasExistingBalance && (
              <div className="space-y-3 pt-2 border-t border-border">
                <div>
                  <Label htmlFor="existingBalance">Existing Loan Balance ({currencySymbol})<InfoTooltip content="The outstanding loan balance already owed against this policy before the new loan." /></Label>
                  <CurrencyInput
                    id="existingBalance"
                    value={data.existingBalance === 0 ? null : data.existingBalance}
                    onChange={(v) => update({ existingBalance: v ?? 0 })}
                    min={0}
                    currency={isUSA ? "USD" : "CAD"}
                    locale={isUSA ? "en-US" : "en-CA"}
                    placeholder="Enter existing balance"
                  />
                </div>

                {data.existingBalance > 0 && (
                  <div className="flex items-center justify-between p-2 rounded-md bg-accent/5 border border-accent/20">
                    <div>
                      <Label className="text-sm font-medium">Consolidate into one repayment plan?</Label>
                      <p className="text-[10px] text-muted-foreground">
                        Combine your existing balance with the new loan into a single repayment structure.
                      </p>
                    </div>
                    <Switch
                      checked={data.consolidateExisting}
                      onCheckedChange={(checked) => update({ consolidateExisting: checked })}
                    />
                  </div>
                )}

                {data.existingBalance > 0 && (
                  <div className="text-xs text-muted-foreground">
                    {data.consolidateExisting ? (
                      <span>✓ Total repayment amount: <span className="font-semibold text-foreground">{fmt(data.loanAmount + data.existingBalance)}</span> (new loan + existing balance)</span>
                    ) : (
                      <span>Repayment covers new loan only: <span className="font-semibold text-foreground">{fmt(data.loanAmount)}</span></span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Multi-Policy Toggle */}
          <div className="space-y-3 p-3 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Are you borrowing from more than one policy?</Label>
                <p className="text-[10px] text-muted-foreground">Toggle on to split this loan across multiple policies.</p>
              </div>
              <Switch
                checked={data.isMultiPolicy}
                onCheckedChange={(checked) => update({ isMultiPolicy: checked })}
              />
            </div>

            {data.isMultiPolicy && (
              <div className="space-y-3 pt-2 border-t border-border">
                <div>
                  <Label className="text-xs">Number of Policies</Label>
                  <Select
                    value={String(data.multiPolicyCount)}
                    onValueChange={(v) => update({ multiPolicyCount: Number(v) })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2 Policies</SelectItem>
                      <SelectItem value="3">3 Policies</SelectItem>
                      <SelectItem value="4">4 Policies</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Policy 1 — auto-calculated from remainder */}
                {(() => {
                  const othersTotal = data.policyLoans.slice(1, data.multiPolicyCount).reduce((s, p) => s + (p.amount || 0), 0);
                  const policy1Amount = Math.max(0, data.loanAmount - othersTotal);
                  const entry = data.policyLoans[0] || { amount: 0, startDate: '', policyNumber: '', useSameStartDate: true, useSameAnniversary: true, anniversaryMonth: data.policyAnniversaryMonth, anniversaryDay: data.policyAnniversaryDay };
                  return (
                    <div className="space-y-2 p-2.5 rounded-md border border-primary/30 bg-primary/5">
                      <p className="text-xs font-semibold">Policy 1 <span className="text-muted-foreground font-normal">(auto-calculated from main loan)</span></p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px]">Loan Amount ({currencySymbol})</Label>
                          <Input
                            type="number"
                            value={policy1Amount || ''}
                            disabled
                            className="bg-muted/50"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Policy # (recommended)</Label>
                          <Input
                            value={entry.policyNumber}
                            onChange={e => {
                              const updated = [...data.policyLoans];
                              updated[0] = { ...entry, policyNumber: e.target.value };
                              update({ policyLoans: updated });
                            }}
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground">Uses main Loan Effective Date and Anniversary Date from Policy Details above.</p>
                    </div>
                  );
                })()}

                {/* Policies 2–4 */}
                {Array.from({ length: data.multiPolicyCount - 1 }, (_, idx) => {
                  const i = idx + 1; // index 1-based in policyLoans array
                  const entry: MultiPolicyEntry = data.policyLoans[i] || { amount: 0, startDate: '', policyNumber: '', useSameStartDate: true, useSameAnniversary: true, anniversaryMonth: data.policyAnniversaryMonth, anniversaryDay: data.policyAnniversaryDay };
                  const updateEntry = (partial: Partial<MultiPolicyEntry>) => {
                    const updated = [...data.policyLoans];
                    updated[i] = { ...entry, ...partial };
                    update({ policyLoans: updated });
                  };
                  return (
                    <div key={i} className="space-y-2 p-2.5 rounded-md border border-border bg-card">
                      <p className="text-xs font-semibold">Policy {i + 1}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px]">Loan Amount ({currencySymbol})</Label>
                          <CurrencyInput
                            value={entry.amount || null}
                            min={0}
                            currency={isUSA ? "USD" : "CAD"}
                            locale={isUSA ? "en-US" : "en-CA"}
                            formatOnBlur={false}
                            onChange={(v) => updateEntry({ amount: v ?? 0 })}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Policy # (recommended)</Label>
                          <Input
                            value={entry.policyNumber}
                            onChange={e => updateEntry({ policyNumber: e.target.value })}
                            placeholder="Optional"
                          />
                        </div>
                      </div>

                      {/* Same start date toggle */}
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!entry.useSameStartDate}
                          onCheckedChange={(checked) => updateEntry({ useSameStartDate: !checked, ...(!checked ? { startDate: '' } : {}) })}
                          className="scale-75"
                        />
                        <Label className="text-[10px]">Choose separate loan start date?</Label>
                      </div>
                      {!entry.useSameStartDate && (
                        <div>
                          <Label className="text-[10px]">Loan Start Date</Label>
                          <Input
                            type="date"
                            value={entry.startDate}
                            onChange={e => updateEntry({ startDate: e.target.value })}
                          />
                        </div>
                      )}

                      {/* Same anniversary toggle */}
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!entry.useSameAnniversary}
                          onCheckedChange={(checked) => updateEntry({ useSameAnniversary: !checked, ...(!checked ? { anniversaryMonth: data.policyAnniversaryMonth, anniversaryDay: data.policyAnniversaryDay } : {}) })}
                          className="scale-75"
                        />
                        <Label className="text-[10px]">Different policy anniversary date?</Label>
                      </div>
                      {!entry.useSameAnniversary && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px]">Anniversary Month</Label>
                            <Select value={String(entry.anniversaryMonth)} onValueChange={v => updateEntry({ anniversaryMonth: Number(v) })}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, mi) => (
                                  <SelectItem key={mi + 1} value={String(mi + 1)}>{m}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[10px]">Anniversary Day</Label>
                            <Input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={entry.anniversaryDay === 0 ? '' : String(entry.anniversaryDay)}
                              onChange={e => {
                                const raw = e.target.value.replace(/[^0-9]/g, '');
                                if (raw === '') { updateEntry({ anniversaryDay: 0 }); return; }
                                updateEntry({ anniversaryDay: Math.min(31, Math.max(0, parseInt(raw, 10))) });
                              }}
                              placeholder="1–31"
                              className="h-8 text-xs"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Validation summary */}
                {(() => {
                  const othersTotal = data.policyLoans.slice(1, data.multiPolicyCount).reduce((s, p) => s + (p.amount || 0), 0);
                  const policy1Amount = Math.max(0, data.loanAmount - othersTotal);
                  const hasError = othersTotal > data.loanAmount + 0.01;
                  const total = policy1Amount + othersTotal;
                  return (
                    <div className={`text-xs p-2 rounded-md ${
                      hasError ? 'bg-destructive/10 text-destructive border border-destructive/30' :
                      othersTotal > 0 ? 'bg-primary/5 text-primary border border-primary/20' :
                      'text-muted-foreground'
                    }`}>
                      {hasError ? (
                        <span>⚠ Policy 2–{data.multiPolicyCount} amounts ({fmt(othersTotal)}) exceed the total loan ({fmt(data.loanAmount)}). Please reduce.</span>
                      ) : othersTotal > 0 ? (
                        <span>✓ Policy 1: {fmt(policy1Amount)} + Others: {fmt(othersTotal)} = {fmt(total)}</span>
                      ) : (
                        <span>Enter amounts for policies 2+ above. Policy 1 will auto-adjust.</span>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Policy Details */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold">Policy Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Anniversary Month
                <InfoTooltip content="Policy loan interest and policy anniversary are usually tied to different dates. The loan begins on the date the loan is processed, while unpaid interest is often adjusted, added, or capitalized on the policy anniversary date. Entering the correct anniversary month and day helps the planner estimate when those policy loan interest adjustments may occur." />
              </Label>
              <Select value={String(data.policyAnniversaryMonth)} onValueChange={v => update({ policyAnniversaryMonth: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Anniversary Day
                <InfoTooltip content="Policy loan interest and policy anniversary are usually tied to different dates. The loan begins on the date the loan is processed, while unpaid interest is often adjusted, added, or capitalized on the policy anniversary date. Entering the correct anniversary month and day helps the planner estimate when those policy loan interest adjustments may occur." />
              </Label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={data.policyAnniversaryDay === 0 ? '' : String(data.policyAnniversaryDay)}
                onChange={e => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  if (raw === '') {
                    update({ policyAnniversaryDay: 0 });
                    return;
                  }
                  const num = Math.min(31, Math.max(0, parseInt(raw, 10)));
                  update({ policyAnniversaryDay: num });
                }}
                placeholder="1–31"
              />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground -mt-2">This helps estimate when policy loan interest may be adjusted or capitalized.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="policyNumber">Policy Number</Label>
              <Input id="policyNumber" value={data.policyNumber} onChange={e => update({ policyNumber: e.target.value })} placeholder="Optional" />
            </div>
            <div>
              <Label htmlFor="lifeInsured">Life Insured / Description</Label>
              <Input id="lifeInsured" value={data.lifeInsured} onChange={e => update({ lifeInsured: e.target.value })} placeholder="Optional" />
            </div>
          </div>

          {/* Investment Purpose Toggle */}
          <div className="space-y-3 p-3 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Is this loan for an investment purpose?</Label>
                <p className="text-[10px] text-muted-foreground">Toggle on to track interest for potential tax deductibility.</p>
              </div>
              <Switch
                checked={data.isInvestmentPurpose}
                onCheckedChange={(checked) => update({ isInvestmentPurpose: checked })}
              />
            </div>
            {data.isInvestmentPurpose && (
              <div className="pt-2 border-t border-border space-y-2">
                <Label className="text-xs">Which interest do you want to track?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => update({ interestTrackingBasis: 'policy' })}
                    className={`rounded-lg border p-2 text-left transition-colors ${
                      data.interestTrackingBasis === 'policy'
                        ? 'border-primary bg-primary/10 ring-1 ring-primary'
                        : 'border-border bg-card hover:border-primary/50'
                    }`}
                  >
                    <p className={`text-xs font-semibold ${data.interestTrackingBasis === 'policy' ? 'text-primary' : 'text-foreground'}`}>Policy Loan Interest</p>
                    <p className="text-[9px] text-muted-foreground mt-0.5">Interest charged by the insurance company</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => update({ interestTrackingBasis: 'intended' })}
                    className={`rounded-lg border p-2 text-left transition-colors ${
                      data.interestTrackingBasis === 'intended'
                        ? 'border-primary bg-primary/10 ring-1 ring-primary'
                        : 'border-border bg-card hover:border-primary/50'
                    }`}
                  >
                    <p className={`text-xs font-semibold ${data.interestTrackingBasis === 'intended' ? 'text-primary' : 'text-foreground'}`}>Intended Loan Interest</p>
                    <p className="text-[9px] text-muted-foreground mt-0.5">Interest at the discipline repayment rate</p>
                  </button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Intentional Repayment Strategy */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold">Intentional Repayment Strategy™</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Mode Selection */}
          <div className="rounded-lg bg-muted/50 border border-border p-4 space-y-3">
            <div className="space-y-1">
              <Label className="text-sm font-semibold">How do you want to solve for your loan?</Label>
              <p className="text-[10px] text-muted-foreground">Choose your preferred approach — you can switch anytime.</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => update({ repaymentMode: 'payment' })}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  isPaymentMode
                    ? 'border-primary bg-primary/10 ring-1 ring-primary'
                    : 'border-border bg-card hover:border-primary/50'
                }`}
              >
                <p className={`text-sm font-semibold ${isPaymentMode ? 'text-primary' : 'text-foreground'}`}>By Payment</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Choose the payment you want to commit to</p>
              </button>
              <button
                type="button"
                onClick={() => update({ repaymentMode: 'rate' })}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  data.repaymentMode === 'rate'
                    ? 'border-primary bg-primary/10 ring-1 ring-primary'
                    : 'border-border bg-card hover:border-primary/50'
                }`}
              >
                <p className={`text-sm font-semibold ${data.repaymentMode === 'rate' ? 'text-primary' : 'text-foreground'}`}>By Interest Rate</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Set a discipline rate and see the resulting payment</p>
              </button>
              <button
                type="button"
                onClick={() => update({ repaymentMode: 'interest_only' })}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  isInterestOnlyMode
                    ? 'border-primary bg-primary/10 ring-1 ring-primary'
                    : 'border-border bg-card hover:border-primary/50'
                }`}
              >
                <p className={`text-sm font-semibold ${isInterestOnlyMode ? 'text-primary' : 'text-foreground'}`}>Interest Only</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Pay only interest — no principal reduction</p>
              </button>
            </div>
          </div>

          {!isModeSelected ? (
            <div className="text-center py-6 text-muted-foreground">
              <p className="text-sm">Select a repayment approach above to continue.</p>
            </div>
          ) : (
          <>
          {/* Precision Mode Toggle */}
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Precision Mode (0.25% increments)</Label>
            <Switch
              checked={data.precisionMode}
              onCheckedChange={(checked) => update({ precisionMode: checked })}
              className="scale-75"
            />
          </div>

          {isPaymentMode ? (
            /* === SOLVE FOR PAYMENT MODE === */
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Frequency<InfoTooltip content="How often you make repayments." /></Label>
                  <Select value={data.paymentFrequency} onValueChange={(v: PaymentFrequency) => update({ paymentFrequency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                      <SelectItem value="semimonthly">Semi-Monthly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="semiannual">Semi-Annual</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="desiredPayment" className={`text-xs font-semibold ${!data.desiredPayment ? 'text-accent' : ''}`}>
                    Target Payment Amount ({currencySymbol})<InfoTooltip content="Enter the clean, rounded payment you'd like to make each period." />
                  </Label>
                  <CurrencyInput
                    id="desiredPayment"
                    value={data.desiredPayment === 0 ? null : data.desiredPayment}
                    onChange={(v) => update({ desiredPayment: v ?? 0 })}
                    min={0}
                    currency={isUSA ? "USD" : "CAD"}
                    locale={isUSA ? "en-US" : "en-CA"}
                    placeholder="Enter amount to begin"
                    className={!data.desiredPayment ? 'border-accent ring-2 ring-accent/30 animate-pulse' : ''}
                  />
                  {!data.desiredPayment && (
                    <p className="text-[10px] text-accent font-medium mt-1">↑ Enter your target payment to generate results</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="startDate" className="text-xs">First Payment Date</Label>
                  <Input id="startDate" type="date" value={data.startDate} onChange={e => update({ startDate: e.target.value })} />
                </div>
              </div>

              {/* Solved Results */}
              {solvedPayment && solvedPayment.found && (
                <div className="space-y-2 p-3 rounded-lg bg-accent/5 border border-accent/20">
                  <p className="text-xs font-semibold text-accent uppercase tracking-wider">Solved Results</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <span className="text-muted-foreground">Desired Payment</span>
                    <span className="font-semibold tabular-nums text-right">{fmt(data.desiredPayment)}</span>

                    <span className="text-muted-foreground">Solved Rate</span>
                    <span className="font-semibold tabular-nums text-right">{(solvedPayment.solvedRate * 100).toFixed(2)}%</span>

                    <span className="text-muted-foreground">Base Payment</span>
                    <span className="font-semibold tabular-nums text-right">{fmt(solvedPayment.basePayment)}</span>

                    <span className="text-muted-foreground">Extra Principal/Period</span>
                    <span className="font-semibold tabular-nums text-right text-positive">{fmt(solvedPayment.extraPrincipal)}</span>

                    <span className="text-muted-foreground">Est. Payoff Length</span>
                    <span className="font-semibold tabular-nums text-right">{solvedPayment.estimatedPayoffPayments} payments</span>

                    <span className="text-muted-foreground">Est. Final Payment</span>
                    <span className="font-semibold tabular-nums text-right">{fmt(solvedPayment.estimatedFinalPayment)}</span>
                  </div>
                </div>
              )}

              <p className="text-[10px] text-muted-foreground leading-relaxed">
                When a clean payment amount is entered, the planner may solve to the nearest valid repayment discipline rate and apply any excess payment as extra principal. This helps create simple, practical payment behavior while paying the loan off slightly faster.
              </p>
            </>
          ) : isInterestOnlyMode ? (
            /* === INTEREST-ONLY MODE === */
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Frequency<InfoTooltip content="How often you make interest-only payments." /></Label>
                  <Select value={data.paymentFrequency} onValueChange={(v: PaymentFrequency) => handleTermChange(data.termYears, data.termMonths, v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                      <SelectItem value="semimonthly">Semi-Monthly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="semiannual">Semi-Annual</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                      <SelectItem value="endofterm">End of Term</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Interest Basis<InfoTooltip content="Choose whether to calculate interest-only payments based on the policy loan rate or the intended loan discipline rate." /></Label>
                  <Select value={data.interestOnlyBasis} onValueChange={(v: 'policy' | 'intended') => update({ interestOnlyBasis: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="policy">Policy Loan Rate ({data.interestRate}%)</SelectItem>
                      <SelectItem value="intended">Intended Rate ({data.disciplineRate}%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="startDate" className="text-xs">First Payment Date</Label>
                  <Input id="startDate" type="date" value={data.startDate} onChange={e => update({ startDate: e.target.value })} />
                </div>
              </div>

              {/* Term selection for interest-only */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs">Term — Years</Label>
                    <span className="text-sm font-semibold text-accent tabular-nums">{data.termYears} yr</span>
                  </div>
                  <Slider
                    value={[data.termYears]}
                    onValueChange={([v]) => handleTermChange(v, data.termMonths)}
                    min={0}
                    max={35}
                    step={1}
                  />
                  {data.termYears > 25 && (
                    <p className="text-[10px] text-destructive font-medium mt-1">
                      Are you sure you want to extend the repayment time frame this long?
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs">Term — Months</Label>
                    <span className="text-sm font-semibold text-accent tabular-nums">{data.termMonths} mo</span>
                  </div>
                  <Slider
                    value={[data.termMonths]}
                    onValueChange={([v]) => handleTermChange(data.termYears, v)}
                    min={0}
                    max={11}
                    step={1}
                  />
                </div>
              </div>

              {/* Interest-only payment summary */}
              {(() => {
                const effectiveExisting = (data.hasExistingBalance && data.consolidateExisting) ? data.existingBalance : 0;
                const totalBorrowed = data.loanAmount + effectiveExisting;
                const rate = data.interestOnlyBasis === 'policy' ? data.interestRate / 100 : data.disciplineRate / 100;
                const ppy = getPaymentsPerYear(data.paymentFrequency);
                const interestOnlyPayment = totalBorrowed * rate / ppy;
                const totalInterestOverTerm = interestOnlyPayment * data.numberOfPayments;

                // Calculate when interest exceeds loan
                let exceedYear = 0;
                let exceedMonth = 0;
                if (totalBorrowed > 0 && interestOnlyPayment > 0) {
                  const paymentsToExceed = Math.ceil(totalBorrowed / interestOnlyPayment);
                  const monthsToExceed = Math.round(paymentsToExceed / ppy * 12);
                  exceedYear = Math.floor(monthsToExceed / 12);
                  exceedMonth = monthsToExceed % 12;
                }
                const interestExceedsLoan = totalInterestOverTerm >= totalBorrowed;

                return (
                  <div className="space-y-3">
                    {/* Principal not being repaid warning */}
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                      <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-destructive">Principal Is Not Being Repaid</p>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                          Interest-only payments cover only the periodic interest charge. <span className="font-semibold text-foreground">No principal is reduced.</span> The full loan balance of <span className="font-semibold text-foreground">{fmt(totalBorrowed)}</span> remains outstanding and is assumed to be repaid as a full lump sum payment at the end of the selected term ({data.termYears} yr {data.termMonths} mo).
                        </p>
                      </div>
                    </div>

                    {/* Interest exceeds loan warning */}
                    {exceedYear > 0 && (
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-300/50 dark:border-amber-700/30">
                        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">Interest Cost Will Exceed Original Loan</p>
                          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                            Total interest cost will equal or exceed the entire original loan amount of <span className="font-semibold text-foreground">{fmt(totalBorrowed)}</span> at approximately <span className="font-semibold text-foreground">{exceedYear} year{exceedYear !== 1 ? 's' : ''}{exceedMonth > 0 ? ` ${exceedMonth} month${exceedMonth !== 1 ? 's' : ''}` : ''}</span>.
                            {interestExceedsLoan && (
                              <span className="font-semibold text-destructive"> Your current term exceeds this threshold.</span>
                            )}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Interest-Only Payment</p>
                        <p className="text-2xl sm:text-3xl font-bold tabular-nums tracking-tight">{fmt(interestOnlyPayment)}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {data.numberOfPayments} payments · No principal reduction
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Total Interest Over Term</p>
                        <p className="text-xl font-semibold tabular-nums">{fmt(totalInterestOverTerm)}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          + {fmt(totalBorrowed)} principal due at end of term
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <p className="text-[10px] text-muted-foreground leading-relaxed">
                Interest-only payments cover the periodic interest charge without reducing the principal balance. This mode is commonly used for investment loans where the borrower intends to repay the principal at the end of the term or through a separate event. The full loan balance remains outstanding throughout the term.
              </p>
            </>
          ) : (
            /* === SOLVE BY RATE MODE === */
            <>
              {/* Discipline Rate + Payment Frequency side by side */}
              <div className="grid grid-cols-[7fr_3fr] gap-3">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs">Repayment Discipline Rate<InfoTooltip content="The interest rate you commit to 'paying yourself' — higher than the policy loan rate." /></Label>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        step={sliderStep}
                        min={7}
                        max={25}
                        value={data.disciplineRate}
                        onChange={e => update({ disciplineRate: Number(e.target.value) })}
                      className="w-[4.5rem] h-7 text-xs text-right tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <span className="text-xs text-muted-foreground">%</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-1.5 text-xs"
                        onClick={() => update({ disciplineRate: 10 })}
                        title="Reset to 10%"
                      >
                        <RotateCcw className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <Slider
                    value={[Math.min(data.disciplineRate, 20)]}
                    onValueChange={([v]) => {
                      const snapped = data.precisionMode ? Math.round(v * 4) / 4 : v;
                      update({ disciplineRate: snapped });
                    }}
                    min={7}
                    max={20}
                    step={sliderStep}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>7%</span>
                    <span>20%</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Frequency<InfoTooltip content="How often you make repayments." /></Label>
                  <Select value={data.paymentFrequency} onValueChange={(v: PaymentFrequency) => handleTermChange(data.termYears, data.termMonths, v)}>
                    <SelectTrigger className="mt-0.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                      <SelectItem value="semimonthly">Semi-Monthly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="semiannual">Semi-Annual</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                      <SelectItem value="endofterm">End of Term</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Amortization Years + Months side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs">Amortization — Years</Label>
                    <span className="text-sm font-semibold text-accent tabular-nums">{data.termYears} yr</span>
                  </div>
                  <Slider
                    value={[data.termYears]}
                    onValueChange={([v]) => handleTermChange(v, data.termMonths)}
                    min={0}
                    max={35}
                    step={1}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>0</span><span>10</span><span>20</span><span>35</span>
                  </div>
                  {data.termYears > 25 && (
                    <p className="text-[10px] text-destructive font-medium mt-1">
                      Are you sure you want to extend the repayment time frame this long?
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs">Amortization — Months</Label>
                    <span className="text-sm font-semibold text-accent tabular-nums">{data.termMonths} mo</span>
                  </div>
                  <Slider
                    value={[data.termMonths]}
                    onValueChange={([v]) => handleTermChange(data.termYears, v)}
                    min={0}
                    max={11}
                    step={1}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>0</span><span>6</span><span>11</span>
                  </div>
                </div>
              </div>

              {/* Total summary */}
              <div className="text-xs text-muted-foreground">
                Total: <span className="font-semibold text-foreground">{data.termYears} yr {data.termMonths} mo</span>
                {' '}= <span className="font-semibold text-foreground">{data.numberOfPayments} {data.paymentFrequency} payments</span>
              </div>

              {/* First Payment Date + Target Payment side by side */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
                <div>
                  <Label htmlFor="startDate" className="text-xs">First Payment Date</Label>
                  <Input id="startDate" type="date" value={data.startDate} onChange={e => update({ startDate: e.target.value })} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Target Payment Per Period</p>
                  <p className="text-2xl sm:text-3xl font-bold tabular-nums tracking-tight">
                    {isPaymentMode && data.desiredPayment > 0 ? fmt(data.desiredPayment) : fmt(targetPayment)}
                  </p>
                </div>
              </div>
            </>
          )}
          </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
